import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import ConfirmationModal from './ConfirmationModal.js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDictation } from '../InstructionAssistant/useDictation.js';
import Waveform from '../InstructionAssistant/Waveform.js';
import ChatThinkingIndicator from './ChatThinkingIndicator.js';
import { createProgressiveTextReveal } from '../../utils/createProgressiveTextReveal.js';
import { useChatStreaming } from '../../hooks/useChatStreaming.js';
import { useStickToBottom } from '../../hooks/useStickToBottom.js';
import { isSummaryPrompt, summaryPrompt } from '../Workspace/summaryPrompt.js';
import WorkspaceIcon from '../Workspace/WorkspaceIcon.js';
import { runAgentTurn, PERMISSION_DECISIONS } from '../ChatAgent/runAgentTurn.js';
import PermissionCard, { AgentActions, QuestionCard, RememberedTools } from '../ChatAgent/PermissionCard.js';
import { askUserTool } from '../ChatAgent/askUserTool.js';
import { readRememberedTools, forgetTool } from '../ChatAgent/toolPermissions.js';

const IconHistory = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
);
const IconTrending = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
);
const IconSummary = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0 1 12 0v2"/></svg>
);
const IconPencil = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
);
const IconClipboard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M9 12h6M9 16h6"/></svg>
);
const IconHEP = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
);
const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
);

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Auto-summary ledger, one marker per conversation. Set when the summary is
// auto-sent AND when the user deletes their chat history - a deliberate
// delete must never trigger an annoying re-fetch, even after a reload.
const AUTO_SUMMARY_KEY_PREFIX = 'chat-auto-summary-';

function hasAutoSummaryMarker(conversationId) {
  try {
    return (
      localStorage.getItem(AUTO_SUMMARY_KEY_PREFIX + conversationId) === '1'
    );
  } catch (err) {
    // Storage unavailable (private mode): err on the quiet side - claiming
    // "already offered" means no auto-summary rather than a repeating one
    return true;
  }
}

// The clinician's Auto-summary switch (top of the chat). Off unless they turn
// it on: auto-summary was switched off for everyone in 98eade097. Its home is
// the account (note_preferences.chat_auto_summary, read back from stats as
// chatAutoSummary); this browser keeps a copy, used alone on a backend that
// predates the setting.
const AUTO_SUMMARY_PREF_KEY = 'comprehendpt_chat_auto_summary';

function readAutoSummaryPref() {
  try {
    return localStorage.getItem(AUTO_SUMMARY_PREF_KEY) === '1';
  } catch (err) {
    return false;
  }
}

function writeAutoSummaryPref(on) {
  try {
    localStorage.setItem(AUTO_SUMMARY_PREF_KEY, on ? '1' : '0');
  } catch (err) {
    // This session only
  }
}

// Ticked checklist items ("- [ ]" in an answer, e.g. the summary's action
// items), per visit: { "<message>:<line>": true }. This browser only.
const CHECKS_KEY_PREFIX = 'comprehendpt_chat_checks_';

/** A message's identity for its ticks: its text, which the server keeps as-is
 *  (the client's timestamps are not the server's, so they can't be the key). */
function messageKeyOf(content) {
  let hash = 5381;
  const text = String(content || '');
  for (let i = 0; i < text.length; i += 1) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0; // eslint-disable-line no-bitwise
  return (hash >>> 0).toString(36); // eslint-disable-line no-bitwise
}

function readChecks(conversationId) {
  try {
    return JSON.parse(localStorage.getItem(CHECKS_KEY_PREFIX + conversationId)) || {};
  } catch (err) {
    return {};
  }
}

function writeChecks(conversationId, checks) {
  try {
    localStorage.setItem(CHECKS_KEY_PREFIX + conversationId, JSON.stringify(checks));
  } catch (err) {
    // This session only
  }
}

/** The switch itself; the dashboard theme has no compact one. */
function Switch({ on }) {
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'relative',
        flex: 'none',
        width: '30px',
        height: '17px',
        borderRadius: '9px',
        background: on ? '#2dce89' : '#d0d5dd',
        transition: 'background .15s',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: on ? '15px' : '2px',
          width: '13px',
          height: '13px',
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,.2)',
          transition: 'left .15s',
        }}
      />
    </span>
  );
}

/**
 * Auto-summary, as the first of the empty chat's suggestions: it's decided
 * once, right when a chat would start, so it lives there rather than taking a
 * corner of every visit. Styled like the prompt rows below it.
 */
function AutoSummaryRow({ on, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        backgroundColor: on ? '#f0fbf6' : '#ffffff',
        border: `1px solid ${on ? '#bfe6d3' : '#e5e7eb'}`,
        borderRadius: '12px',
        padding: '12px 16px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color .15s, border-color .15s',
      }}
    >
      <WorkspaceIcon id="summary" size={18} style={{ color: on ? '#14724F' : '#6b7280' }} />
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1px' }}>
        <span style={{ fontSize: '0.9rem', color: '#1f2937' }}>Start each visit with a summary</span>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Today's focus and action items, before you ask</span>
      </span>
      <Switch on={on} />
    </button>
  );
}

function writeAutoSummaryMarker(conversationId) {
  try {
    localStorage.setItem(AUTO_SUMMARY_KEY_PREFIX + conversationId, '1');
  } catch (err) {
    // Best effort - the in-memory guard still prevents repeats this session
  }
}

// Enter sends; Shift+Enter (a new line) and the Enter that confirms an IME
// composition do not
export const isSendKey = (event) =>
  event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing && event.keyCode !== 229;

// What the assistant is doing while a chat tool runs (agentic chat)
const AGENT_ACTIVITY = {
  get_note_sections: 'Reading the note…',
  load_template: 'Loading your template…',
  get_template: 'Reading your template…',
  comprehend_note: 'Running Comprehend…',
  write_section: 'Writing to the EMR…',
  add_visit_information: 'Adding visit information…',
  update_instructions: 'Updating instructions…',
  set_reply_type: 'Updating the template…',
  instruction_assistant: 'Working out template changes…',
  edit_examples: 'Updating examples…',
  undo_change: 'Undoing…',
  get_change: 'Looking back…',
  ask_user: 'Waiting for your answer…',
};

// Above the window's fixed Help (9999999) and Playground checklist (10000000) buttons
const INPUT_ABOVE_FLOATING_Z = 10000001;

/**
 * agentTools: when given (the EMR visit view, behind `agentic_chat`), questions
 * go to the agentic chat, which can use these tools on the note. Each change
 * asks first; see components/ChatAgent.
 * inVisitPanel: the extension visit view. The input stays at the bottom of the
 * window, and while the chat is shown the Help button and onboarding
 * checklist (fixed to the bottom corners) are hidden; the input is layered
 * over them anyway in case they show.
 */
const Chat = ({ conversationId, apiClient, groupId, isActive = true, agentTools = null, agentContext = [], inVisitPanel = false }) => {
  const patient = apiClient?.patients?.find(
    (patient) => patient.uid === groupId,
  );
  const firstName = patient?.firstName || null;
  const lastName = patient?.lastName ? ' ' + patient.lastName : '';
  const patientName = patient ? `${patient.firstName}${lastName}` : null;
  const [chatHistory, setChatHistory] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false); // This is to handle Trash icon display
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [recSeconds, setRecSeconds] = useState(0);

  const chatContainerRef = useRef(null);
  const listRef = useRef(null);
  const inputRowRef = useRef(null);
  // inVisitPanel: the input is fixed to the bottom of the window, lined up with
  // the chat column; a spacer of its height keeps the last message above it
  const [inputBox, setInputBox] = useState(null);
  useLayoutEffect(() => {
    if (!inVisitPanel || !isActive) return undefined;
    const measure = () => {
      if (!listRef.current) return;
      const { left, width } = listRef.current.getBoundingClientRect();
      const height = inputRowRef.current?.offsetHeight || 0;
      setInputBox((prev) => (prev && prev.left === left && prev.width === width && prev.height === height ? prev : { left, width, height }));
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) {
      observer.observe(listRef.current);
      if (inputRowRef.current) observer.observe(inputRowRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [inVisitPanel, isActive]);
  useEffect(() => {
    if (!inVisitPanel || !isActive) return undefined;
    document.body.classList.add('cpt-chat-open');
    return () => document.body.classList.remove('cpt-chat-open');
  }, [inVisitPanel, isActive]);
  const textareaRef = useRef(null);
  // Keep streamed responses pinned unless the user scrolls up to read.
  // windowScroll: messages flow in the page (like the other tabs) and the PAGE
  // scrolls; chatContainerRef is the visibility anchor for the chat tab.
  const { scrollIfPinned, pin } = useStickToBottom(chatContainerRef, {
    windowScroll: true,
  });
  const activeTextRevealRef = useRef(null);
  const isMountedRef = useRef(false);
  const scrollToTopAfterClearRef = useRef(false);
  const chatStreaming = useChatStreaming(apiClient);
  // Distinguishes "history not fetched yet" from "fetched and empty" - the
  // auto-summary must only consider the latter
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // In-memory backstop for the localStorage marker (per conversation)
  const autoSummaryAttemptedRef = useRef(new Set());

  // Agentic chat: tools, remembered "Always allow" choices, and a Stop
  const agentMode = Array.isArray(agentTools) && agentTools.length > 0;
  const userEmail = apiClient?.sessionEmail;
  const [rememberedTools, setRememberedTools] = useState(() => readRememberedTools(userEmail));
  const agentStoppedRef = useRef(false);
  // The latest history for findAction, which runs long after a turn starts
  const chatHistoryRef = useRef([]);
  chatHistoryRef.current = chatHistory;
  const pendingPermissionsRef = useRef(new Set());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (activeTextRevealRef.current) {
        activeTextRevealRef.current.cancel();
        activeTextRevealRef.current = null;
      }
    };
  }, []);

  // Clear/Delete Chat confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => {});

  const requestDeleteChat = (event) => {
    event.stopPropagation(); // Prevent triggering row click
    setIsModalOpen(true); // Open the modal
    setConfirmAction(() => () => {
      deleteChatHistory(conversationId);
      setIsModalOpen(false);
    });
  };

  const handleInputChange = (e) => {
    setQuestion(e.target.value);
    setIsTyping(e.target.value.trim().length > 0); // Detect typing
  };

  const handleCopy = (index, content) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2_000);
    });
  };

  const deleteChatHistory = (conversationId) => {
    apiClient
      .clearChatHistory(conversationId)
      .then(() => {
        // Deleting history is deliberate - never auto-fetch a new summary
        // into the conversation the user just cleared
        writeAutoSummaryMarker(conversationId);
        scrollToTopAfterClearRef.current = true;
        setChatHistory([]);
      })
      .catch((error) => {
        // probably no session key
        console.log(error);
        console.log('Did not delete conversation');
      });
  };

  // Dictate into the question field via mic; transcript is appended, never replaces typing.
  // Guards against a transcription that resolves after the user already hit Cancel.
  const dictationCancelledRef = useRef(false);
  const { isRecording, isTranscribing, mediaStream, toggle, stop, cancel } =
    useDictation(apiClient, (transcript) => {
      if (dictationCancelledRef.current) {
        dictationCancelledRef.current = false;
        return;
      }
      if (transcript) {
        sendQuestion(question ? `${question} ${transcript}` : transcript);
      }
    });

  const cancelDictation = () => {
    dictationCancelledRef.current = true;
    cancel();
  };

  // Count up the elapsed recording time while dictating.
  useEffect(() => {
    if (!isRecording) {
      setRecSeconds(0);
      return undefined;
    }
    setRecSeconds(0);
    const id = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  // resize textarea automatically for question insert
  useEffect(() => {
    if (textareaRef.current) {
      if (question) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      } else {
        textareaRef.current.style.height = '';
      }
    }
  }, [question]);

  useEffect(() => {
    if (!isActive) return undefined;

    const frameId = requestAnimationFrame(() => {
      pin();
    });
    return () => cancelAnimationFrame(frameId);
  }, [isActive, pin]);

  useEffect(() => {
    if (isActive) {
      if (scrollToTopAfterClearRef.current && chatHistory.length === 0) {
        scrollToTopAfterClearRef.current = false;
        requestAnimationFrame(() => {
          const top = chatContainerRef.current
            ? chatContainerRef.current.getBoundingClientRect().top + window.scrollY
            : 0;
          window.scrollTo({ top, behavior: 'auto' });
        });
        return;
      }
      scrollIfPinned();
    }
  }, [chatHistory, isActive, scrollIfPinned]);

  // Fetch chat history when the component loads
  useEffect(() => {
    console.log('Fetching chat history for conversationId:', conversationId);
    setHistoryLoaded(false);
    if (conversationId) {
      apiClient
        .getChatHistory(conversationId)
        .then(({ chat }) => {
          console.log('Fetched chat history:', chat);
          setChatHistory(chat || []); // Set fetched chat history
          setHistoryLoaded(true);
        })
        .catch((err) => {
          console.error('Error fetching chat history:', err);
          setChatHistory([]); // Fallback to empty array on error
        });
    } else {
      console.warn('Invalid conversation ID:', conversationId);
      setChatHistory([]); // Handle invalid or default conversation ID
    }
  }, [conversationId]);

  // Auto-summary: with the switch on, the first time the chat opens for a
  // visit it asks for the brief (summaryPrompt) — turning it on summarizes
  // this visit now if it hasn't been. Once per visit: the marker is also
  // written when the history is deleted, so clearing never re-triggers it.
  const [autoSummaryOn, setAutoSummaryOn] = useState(readAutoSummaryPref);
  // The account's answer: nothing auto-runs until it's in (a stale copy here
  // mustn't start a summary the account has turned off).
  const [autoSummaryKnown, setAutoSummaryKnown] = useState(!apiClient?.getStats);
  const accountHasAutoSummary = useRef(false);
  useEffect(() => {
    if (!apiClient?.getStats) return undefined;
    let live = true;
    Promise.resolve(apiClient.getStats())
      .then((stats) => {
        if (!live || typeof stats?.chatAutoSummary !== 'boolean') return;
        accountHasAutoSummary.current = true;
        setAutoSummaryOn(stats.chatAutoSummary);
        writeAutoSummaryPref(stats.chatAutoSummary);
      })
      .catch(() => {})
      .finally(() => live && setAutoSummaryKnown(true));
    return () => {
      live = false;
    };
  }, [apiClient]);
  const autoPrompt = autoSummaryOn && autoSummaryKnown ? summaryPrompt(firstName) : null;
  const setAutoSummary = (on) => {
    setAutoSummaryOn(on);
    writeAutoSummaryPref(on);
    if (accountHasAutoSummary.current) {
      apiClient.editUserSettings?.({ note_preferences: { chat_auto_summary: on } })?.catch?.(() => {});
    }
  };

  const [checks, setChecks] = useState({});
  useEffect(() => {
    setChecks(conversationId ? readChecks(conversationId) : {});
  }, [conversationId]);
  const toggleCheck = (key) =>
    setChecks((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next[key]) delete next[key];
      writeChecks(conversationId, next);
      return next;
    });
  useEffect(() => {
    if (!autoPrompt || !isActive || !historyLoaded || !conversationId || loading) return;
    if (hasAutoSummaryMarker(conversationId)) return;
    if (autoSummaryAttemptedRef.current.has(conversationId)) return;
    autoSummaryAttemptedRef.current.add(conversationId);
    writeAutoSummaryMarker(conversationId);
    sendQuestion(autoPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrompt, isActive, historyLoaded, conversationId, loading]);

  // Coming back to the tab shows anything asked meanwhile from another copy
  // of this visit (a second window, the side panel vs the web app). Skipped
  // while an answer is in flight, so the refetch can't replace the bubble it
  // is filling.
  const loadingRef = useRef(false);
  loadingRef.current = loading;
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    const cameBack = isActive && !wasActiveRef.current;
    wasActiveRef.current = isActive;
    if (!cameBack || !conversationId || loadingRef.current) return;
    apiClient
      .getChatHistory(conversationId)
      .then(({ chat }) => {
        if (isMountedRef.current && !loadingRef.current) setChatHistory(chat || []);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Send a new question
  const sendQuestion = (textOverride) => {
    const text = textOverride ?? question;
    if (loading || !text.trim()) return;

    setLoading(true);

    // Add user question and a placeholder response for the assistant
    const userEntry = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    const placeholderEntry = {
      role: 'assistant',
      content: '...', // Placeholder response
      timestamp: new Date().toISOString(),
    };

    pin();
    setChatHistory((prev) => [...prev, userEntry, placeholderEntry]);
    setQuestion('');
    setIsTyping(false); // Reset typing state
    const replacePlaceholder = (replacement) => {
      if (!isMountedRef.current) return;
      setChatHistory((prev) =>
        prev.map((entry) => (entry === placeholderEntry ? replacement : entry)),
      );
    };

    if (agentMode) {
      runAgent(text, placeholderEntry, replacePlaceholder);
      return;
    }

    // Streamed tokens progressively fill the placeholder bubble by mutating its
    // content and re-rendering; the placeholder object itself stays in the
    // history array so replacePlaceholder/pollForChatResponse still match it.
    // The resolved REST response remains the authoritative final answer.
    if (activeTextRevealRef.current) {
      activeTextRevealRef.current.cancel();
      activeTextRevealRef.current = null;
    }
    let streamedText = '';
    const textReveal = createProgressiveTextReveal({
      isActive: () => isMountedRef.current,
      onReveal: (content) => {
        placeholderEntry.content = content;
        setChatHistory((prev) => [...prev]);
      },
    });
    activeTextRevealRef.current = textReveal;

    const ask = async () => {
      return chatStreaming.askQuestion(
        conversationId,
        text,
        (chunk) => {
          streamedText += chunk;
          textReveal.queue(streamedText);
        },
        (response) => {
          if (response) {
            textReveal.queue(response);
          }
        },
      );
    };

    ask()
      .then(async (response) => {
        chatStreaming.clearHandlers();
        if (!isMountedRef.current) return;
        const assistantResponse = {
          role: 'assistant',
          content:
            response.response ||
            'Sorry, the transcript doesn’t contain that information.',
          timestamp: new Date().toISOString(),
        };
        if (streamedText) {
          await textReveal.finish(assistantResponse.content);
          if (!isMountedRef.current) return;
        }
        replacePlaceholder(assistantResponse);
      })
      .catch(async (err) => {
        console.error('Error sending question:', err);
        if (!isMountedRef.current) {
          chatStreaming.clearHandlers();
          textReveal.cancel();
          return;
        }
        if (apiClient.isTimeoutError(err)) {
          chatStreaming.clearHandlers();
          textReveal.cancel();
          pollForChatResponse(conversationId, placeholderEntry);
        } else {
          chatStreaming.clearHandlers();
          textReveal.cancel();
          replacePlaceholder({
            ...placeholderEntry,
            content: 'Error fetching response.',
          });
        }
      })
      .finally(() => {
        chatStreaming.clearHandlers();
        if (activeTextRevealRef.current === textReveal) {
          activeTextRevealRef.current = null;
        }
        if (isMountedRef.current) {
          setLoading(false);
        }
      });
  };

  // Cards (permission questions, auto-allowed changes) go just above the
  // thinking bubble, so the bubble stays last while the turn runs.
  const insertBeforePlaceholder = (placeholderEntry, entry) =>
    setChatHistory((prev) => {
      const at = prev.indexOf(placeholderEntry);
      return at < 0 ? [...prev, entry] : [...prev.slice(0, at), entry, ...prev.slice(at)];
    });

  function runAgent(text, placeholderEntry, replacePlaceholder) {
    agentStoppedRef.current = false;
    const setActivity = (activity) => {
      placeholderEntry.activity = activity;
      if (isMountedRef.current) setChatHistory((prev) => [...prev]);
    };
    const requestPermission = (request) =>
      new Promise((resolve) => {
        const entry = { role: 'permission', request, decision: null };
        setActivity('Waiting for your OK…');
        entry.decide = (answer) => {
          const decision = typeof answer === 'object' ? answer?.decision : answer;
          if (entry.decision) return;
          entry.decision = decision;
          setActivity(decision === PERMISSION_DECISIONS.deny ? 'Thinking…' : AGENT_ACTIVITY[request.tool] || 'Working…');
          pendingPermissionsRef.current.delete(entry);
          if (isMountedRef.current) setChatHistory((prev) => [...prev]);
          resolve(answer);
        };
        pendingPermissionsRef.current.add(entry);
        if (isMountedRef.current) insertBeforePlaceholder(placeholderEntry, entry);
        else entry.decide(PERMISSION_DECISIONS.deny);
      });
    // ask_user: a question card with clickable options, answered in the chat
    const askUser = ({ question, options }) =>
      new Promise((resolve) => {
        const entry = { role: 'question', question, options, answer: null };
        entry.respond = (reply) => {
          if (entry.answer) return;
          entry.answer = reply;
          pendingPermissionsRef.current.delete(entry);
          if (isMountedRef.current) setChatHistory((prev) => [...prev]);
          resolve(reply);
        };
        // Stop answers it too
        entry.decide = () => entry.respond({ cancelled: true });
        pendingPermissionsRef.current.add(entry);
        if (isMountedRef.current) insertBeforePlaceholder(placeholderEntry, entry);
        else entry.respond({ cancelled: true });
      });
    const onActivity = (event) => {
      if (!isMountedRef.current) return;
      if (event.type === 'remembered') {
        setRememberedTools(readRememberedTools(userEmail));
      } else if (event.type === 'tool_start') {
        setActivity(AGENT_ACTIVITY[event.tool] || 'Working…');
      } else if (event.type === 'tool_end') {
        // The model is choosing the next step
        setActivity('Thinking…');
      } else if (event.type === 'auto_allowed') {
        // "Always allow" skips the question, never the record of what changed
        insertBeforePlaceholder(placeholderEntry, {
          role: 'permission',
          request: event.preview,
          decision: PERMISSION_DECISIONS.always,
        });
      }
    };

    runAgentTurn({
      apiClient,
      conversationId,
      question: text,
      tools: [...agentTools, askUserTool],
      context: agentContext,
      askUser,
      userEmail,
      requestPermission,
      onActivity,
      isCancelled: () => agentStoppedRef.current || !isMountedRef.current,
      findAction: (id) => {
        for (const entry of chatHistoryRef.current) {
          const found = entry.actions?.find?.((action) => action.id === id);
          if (found) return found;
        }
        return null;
      },
    })
      .then((result) => {
        replacePlaceholder({
          role: 'assistant',
          content: result.cancelled ? 'Stopped.' : result.response || 'Done.',
          actions: result.actions,
          timestamp: new Date().toISOString(),
        });
      })
      .catch((err) => {
        console.error('Agentic chat failed:', err);
        replacePlaceholder({
          role: 'assistant',
          content: `Sorry, something went wrong: ${err?.message || 'please try again.'}`,
          timestamp: new Date().toISOString(),
        });
      })
      .finally(() => {
        if (isMountedRef.current) setLoading(false);
      });
  }

  const stopAgent = () => {
    agentStoppedRef.current = true;
    pendingPermissionsRef.current.forEach((entry) => entry.decide(PERMISSION_DECISIONS.deny));
  };

  const forgetRememberedTool = (permissionKey) => {
    forgetTool(userEmail, permissionKey);
    setRememberedTools(readRememberedTools(userEmail));
  };

  function pollForChatResponse(conversationId, placeholderEntry) {
    // Notify the user that a response is delayed
    const waitEntry = {
      role: 'assistant',
      content: 'Almost there...', // Placeholder response
      timestamp: new Date().toISOString(),
    };
    let assistantResponse = {};

    setChatHistory((prev) =>
      prev.map((entry) => (entry === placeholderEntry ? waitEntry : entry)),
    );

    let count = 0;
    let interval = setInterval(async () => {
      count += 1;

      try {
        const details = await apiClient.getConversationDetails(conversationId);

        // Check if the assistant's response has been added
        const chat = details.chat || [];
        const lastMessage = chat[chat.length - 1];
        const secondLastMessage = chat[chat.length - 2];
        if (
          lastMessage?.role === 'assistant' &&
          secondLastMessage?.role === 'user'
        ) {
          clearInterval(interval);
          assistantResponse = {
            role: 'assistant',
            content:
              lastMessage.content ||
              'The transcript does not contain the requested information.', // Response from the API
            timestamp: new Date().toISOString(),
          };
          // Replace the placeholder with the actual response
          setChatHistory((prev) =>
            prev.map((entry) =>
              entry === waitEntry ? assistantResponse : entry,
            ),
          );
          return;
        }
      } catch (error) {
        console.log('Error while polling for chat response:', error);
      }

      if (count >= 3) {
        assistantResponse = {
          role: 'assistant',
          content:
            'Sorry, the requested information isn’t available in the transcript.', // Response from the API
          timestamp: new Date().toISOString(),
        };
        // Stop polling after 2 minutes (8 attempts every 15s)
        clearInterval(interval);
        setChatHistory((prev) =>
          prev.map((entry) =>
            entry === waitEntry ? assistantResponse : entry,
          ),
        );
      }
    }, 15000);
  }

  // Drops the blank line between consecutive list items, and between a paragraph and
  // the list right after it, so markdown treats the list as "tight" (plain <li>)
  // instead of "loose" (a <p> per item, which doubles up the spacing). Only checks
  // the NEXT line, not the previous one: dropping the blank line after a list's last
  // item would make the following paragraph a "lazy continuation" of that list item
  // instead of its own block, silently merging the two.
  function tightenLists(markdownText) {
    const isListItem = (line) => /^\s*(?:[-*+]\s|\d+[.)]\s)/.test(line);
    const lines = markdownText.split('\n');
    const result = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.trim() === '') {
        const nextLine = lines.slice(i + 1).find((l) => l.trim() !== '') || '';
        if (isListItem(nextLine)) {
          continue; // drop this blank line
        }
      }
      result.push(line);
    }
    return result.join('\n');
  }

  function flattenListToParagraphs(markdownText) {
    return tightenLists(
      markdownText
        // Merge list item number and bolded header onto same line
        .replaceAll(/^(\d+)\.\s+\*\*(.+?)\*\*\s*\n\s+/gm, '**$1. $2** ') // Removes newlines after numbered headings
        .replaceAll(/\n{2,}/g, '\n\n'), // Clean up multiple breaks to keep paragraphs
    );
  }

  const customChatComponents = {
    p: ({ children }) => (
      <p style={{ marginBottom: '6px', lineHeight: '1.45' }}>{children}</p>
    ),
    strong: ({ children }) => (
      <strong style={{ color: '#244c73', fontWeight: 600 }}>{children}</strong>
    ),
    ul: ({ children }) => (
      <ul style={{ margin: '4px 0', paddingLeft: '18px' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: '4px 0', paddingLeft: '18px' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: '2px', lineHeight: '1.45' }}>{children}</li>
    ),
  };

  // Checklist items ("- [ ] …") render as a real checkbox beside their text,
  // not a bullet plus a disabled grey box; ticks are kept per visit.
  const componentsFor = (messageKey) => ({
    ...customChatComponents,
    li: ({ children, className, node }) => {
      if (!String(className || '').includes('task-list-item')) return customChatComponents.li({ children });
      const parts = React.Children.toArray(children);
      const boxAt = parts.findIndex((part) => part?.props?.type === 'checkbox');
      // The item's own place in the message, so each box ticks on its own.
      const key = `${messageKey}:${node?.position?.start?.line ?? ''}:${node?.position?.start?.column ?? ''}`;
      const done = Boolean(checks[key]);
      return (
        <li style={{ listStyle: 'none', marginLeft: '-18px', marginBottom: '6px', lineHeight: '1.45' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', margin: 0, fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={done}
              onChange={() => toggleCheck(key)}
              style={{ marginTop: '4px', flex: 'none', cursor: 'pointer', accentColor: '#2dce89', width: '15px', height: '15px' }}
            />
            <span style={{ textDecoration: done ? 'line-through' : 'none', color: done ? '#98a2b3' : undefined }}>
              {parts.filter((_, i) => i !== boxAt)}
            </span>
          </label>
        </li>
      );
    },
  });

  // Compact circular icon buttons for the input row - full-size bootstrap
  // buttons eat too much width on the narrow extension side panel
  const iconButtonStyle = {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    flexShrink: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    marginLeft: 0,
    marginRight: 0,
  };

  const textareaPlaceholder =
    chatHistory?.length === 0
      ? patientName
        ? `Ask about ${patientName}-- no chart review needed`
        : 'Ask Comprehend anything-- no chart review needed'
      : 'Ask a follow-up question';
  return (
    <div
      className="list-container"
      ref={listRef}
    >
      <div
        // Visibility anchor for scroll pinning; the PAGE scrolls the messages
        // (no height cap), so the chat gets the full viewport like other tabs
        ref={chatContainerRef}
        style={{
          padding: '1px clamp(12px, 5vw, 36px)',
          ...(inVisitPanel && { paddingBottom: (inputBox?.height || 60) + 8 }),
        }}
      >
        {chatHistory.length === 0 && (
          <>
            <div
              style={{
                textAlign: 'center',
                marginBottom: '20px',
              }}
            >
              <i
                className="fa fa-comments"
                style={{
                  fontSize: '2rem',
                  marginBottom: '10px',
                }}
              ></i>{' '}
              {''}
              <h4
                style={{
                  fontSize: '1.2rem',
                  margin: '5px 0',
                  color: '#6c757d',
                }}
              >
                {firstName
                  ? `Ask questions about ${firstName} or try an example`
                  : 'Ask patient-related questions or try an example'}
              </h4>
              {agentMode && (
                <p style={{ fontSize: '0.82rem', color: '#667085', margin: '4px 0 0' }}>
                  I can also explain how a section came out and change the note or your template. I ask before every change.
                </p>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginBottom: '10px',
                width: '100%',
              }}
            >
              <AutoSummaryRow on={autoSummaryOn} onChange={setAutoSummary} />
              {[
                {
                  label: 'What happened last session?',
                  content: 'What happened last session?',
                  icon: <IconHistory />,
                },
                {
                  label: firstName
                    ? `How is ${firstName} progressing?`
                    : 'How is the patient progressing?',
                  content: firstName
                    ? `how is ${firstName} progressing?`
                    : 'How is the patient progressing?',
                  icon: <IconTrending />,
                },
                {
                  label: firstName
                    ? `Give me a summary of ${firstName}`
                    : 'Give me a patient summary',
                  content: firstName
                    ? `Give me a quick summary of ${firstName}: why they're here, current status, and anything important for today's visit.`
                    : "Give me a quick summary of this patient: why they're here, current status, and anything important for today's visit.",
                  icon: <IconSummary />,
                },
                {
                  label: 'Help me write a note or letter',
                  content: 'Help me write a note or letter',
                  icon: <IconPencil />,
                },
                {
                  label: firstName
                    ? `Generate a HEP for ${firstName}`
                    : 'Generate a HEP',
                  content: firstName
                    ? `Generate a home exercise program (HEP) for ${firstName} based on their diagnosis, current status, and treatment so far.`
                    : 'Generate a home exercise program (HEP) for this patient based on their diagnosis, current status, and treatment so far.',
                  icon: <IconHEP />,
                },
                {
                  label: 'Suggest CPT codes',
                  content:
                    "Determine and substantiate the most suitable CPT billing code for this patient encounter. Furthermore, create and rationalize 5 additional CPT codes that might be regarded as alternate or supplementary billing codes in a numbered list. How do the descriptions and criteria for these codes align with the patient's symptoms, status, and the delivered care during the visit? Confirm that your choices are in alignment with the documented clinical data and shed light on how each code pertains to specific facets of the patient's diagnosis and management.If amount of time mentioned in transcript, assign number of units per code based on the 8 minute rule. 0-7 minutes- 0 units, 8-22 minutes 1 unit, 23-37 minutes 2 units, 38-52 minutes 3 units, 53-67 minutes 4 units, 68-82 minutes 5 units",
                  icon: <IconClipboard />,
                },
              ].map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => sendQuestion(prompt.content)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '14px 16px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    color: '#1f2937',
                    textAlign: 'left',
                    gap: '12px',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {prompt.icon}
                    {prompt.label}
                  </span>
                  <IconChevronRight />
                </button>
              ))}
            </div>
          </>
        )}
        {agentMode && (
          <RememberedTools remembered={rememberedTools} onForget={forgetRememberedTool} />
        )}
        {chatHistory.map((entry, index) => entry.role === 'question' ? (
          <div key={index} style={{ marginBottom: '12px' }}>
            <QuestionCard
              question={entry.question}
              options={entry.options}
              answer={entry.answer}
              onAnswer={(reply) => entry.respond?.(reply)}
            />
          </div>
        ) : entry.role === 'permission' ? (
          <div key={index} style={{ marginBottom: '12px' }}>
            <PermissionCard
              request={entry.request}
              decision={entry.decision}
              onDecide={(decision) => entry.decide?.(decision)}
            />
          </div>
        ) : (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: entry.role === 'user' ? 'row-reverse' : 'row',
              marginBottom: entry.role === 'user' ? '8px' : '14px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: entry.role === 'user' ? '86%' : '100%',
                backgroundColor:
                  entry.role === 'user' ? '#eaf7ef' : 'transparent',
                color: entry.role === 'user' ? '#1f3d2b' : '#263238',
                padding:
                  entry.role === 'user'
                    ? 'clamp(8px, 2vw, 12px) clamp(12px, 4vw, 22px)'
                    : '2px 0',
                borderRadius: entry.role === 'user' ? '14px' : 0,
                border: 'none',
                boxShadow: 'none',
                fontSize: '0.92rem',
                lineHeight: '1.45',
                // Assistant messages are markdown-rendered (react-markdown inserts
                // newline text nodes between blocks); 'pre-wrap' would render those as
                // phantom blank lines, so use 'normal' and let markdown control layout.
                // User messages keep 'pre-wrap' to preserve their own typed line breaks.
                whiteSpace: entry.role === 'user' ? 'pre-wrap' : 'normal',
                wordBreak: 'break-word', // Break long words
              }}
            >
              {entry.role === 'assistant' && entry.content === '...' ? (
                <ChatThinkingIndicator status={entry.activity} />
              ) : entry.role === 'user' && isSummaryPrompt(entry.content) ? (
                // The auto-summary ask, as a chip: the brief is the point, not its instructions.
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                  <span title={entry.content} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                    <WorkspaceIcon id="summary" size={15} />
                    Summary for today
                  </span>
                  {/* The switch lives with what it made: turn it off from right here. */}
                  {autoSummaryOn && (
                    <span style={{ fontSize: '0.72rem', fontWeight: 400, color: '#4b6b57' }}>
                      Auto-summary ·{' '}
                      <button
                        type="button"
                        onClick={() => setAutoSummary(false)}
                        style={{ border: 0, background: 'transparent', padding: 0, color: '#14724F', fontWeight: 600, fontSize: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Turn off
                      </button>
                    </span>
                  )}
                </span>
              ) : (
                <div className="chat-markdown">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={componentsFor(messageKeyOf(entry.content))}
                  >
                    {flattenListToParagraphs(entry.content)}
                  </ReactMarkdown>
                  {entry.role === 'assistant' && (
                    <AgentActions
                      actions={entry.actions}
                      onUndo={
                        agentMode && !loading
                          ? (action) => sendQuestion(`Undo this change: ${action.summary} (action_id ${action.id})`)
                          : null
                      }
                    />
                  )}
                </div>
              )}
              {entry.role === 'assistant' &&
                entry.content !== '...' &&
                entry.content !== 'Almost there...' && (
                  <div
                    onClick={() => handleCopy(index, entry.content)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '5px',
                      cursor: 'pointer',
                      fontSize: '0.78rem',
                      color: copiedIndex === index ? '#238a4b' : '#667085',
                      opacity: 0.75,
                      marginTop: '6px',
                    }}
                  >
                    <i
                      className={
                        copiedIndex === index ? 'fa fa-check' : 'fa fa-copy'
                      }
                    ></i>
                    <span>{copiedIndex === index ? 'Copied' : 'Copy'}</span>
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>

      <div
        ref={inputRowRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px', // Ensure consistent spacing between elements
          padding: '4px 8px 8px 8px',
          // Keep the input reachable while the page scrolls the message history;
          // in the visit panel it never leaves the bottom of the window
          ...(inVisitPanel && inputBox
            ? { position: 'fixed', left: inputBox.left, width: inputBox.width, boxSizing: 'border-box' }
            : { position: 'sticky' }),
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: inVisitPanel ? INPUT_ABOVE_FLOATING_Z : 2,
        }}
      >
        {isRecording ? (
          <div
            style={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              backgroundColor: '#f1f3f5',
              borderRadius: '20px',
              padding: '8px 12px',
            }}
          >
            <button
              onClick={cancelDictation}
              title="Cancel"
              aria-label="Cancel"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: '#868e96',
                fontSize: '1rem',
                padding: 0,
                display: 'flex',
              }}
            >
              <i className="fa fa-times"></i>
            </button>
            <Waveform stream={mediaStream} />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#495057',
                fontWeight: 'bold',
                fontSize: '0.9rem',
              }}
            >
              <i
                className="fa fa-circle text-danger"
                style={{ fontSize: 8 }}
              ></i>
              <span>{formatTime(recSeconds)}</span>
            </div>
            <Button
              color="success"
              size="sm"
              onClick={stop}
              style={{ borderRadius: '20px' }}
            >
              Done
            </Button>
          </div>
        ) : isTranscribing ? (
          <div
            style={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              backgroundColor: '#f1f3f5',
              borderRadius: '20px',
              padding: '8px 12px',
              color: '#495057',
              fontWeight: 'bold',
            }}
          >
            <button
              onClick={cancelDictation}
              title="Cancel"
              aria-label="Cancel"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: '#868e96',
                fontSize: '1rem',
                padding: 0,
                display: 'flex',
              }}
            >
              <i className="fa fa-times"></i>
            </button>
            <i className="fa fa-spinner fa-spin"></i>
            <span>Transcribing...</span>
          </div>
        ) : (
          <>
            <Button
              color="secondary"
              onClick={toggle}
              disabled={loading}
              title="Dictate"
              aria-label="Dictate"
              style={iconButtonStyle}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <i className="fa fa-microphone" style={{ fontSize: '0.9rem' }}></i>
            </Button>
            <textarea
              ref={textareaRef}
              value={question}
              onChange={handleInputChange}
              // onChange={(e) => setQuestion(e.target.value)}
              placeholder={textareaPlaceholder}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter is a new line. Enter that confirms
                // an IME composition (e.g. Japanese input) is not a send.
                if (isSendKey(e)) {
                  e.preventDefault();
                  sendQuestion();
                }
              }}
              style={{
                flexGrow: 1,
                border: '2px solid #6bd888',
                borderRadius: '9px',
                padding: '8px 12px',
                fontSize: '16px',
                lineHeight: '1.4',
                resize: 'none', // Prevent manual resizing
                overflowY: 'auto', // Enable vertical scrollbars
                maxHeight: '120px', // Approximate height for 5 lines
              }}
              rows={1} // Start with one row
              onInput={(e) => {
                e.target.style.height = 'auto'; // Reset height
                e.target.style.height = `${e.target.scrollHeight}px`; // Adjust to fit content
              }}
            />
            {agentMode && loading ? (
              <Button
                color="secondary"
                onClick={stopAgent}
                title="Stop"
                aria-label="Stop"
                style={iconButtonStyle}
              >
                <i className="fa fa-stop" style={{ fontSize: '0.9rem' }}></i>
              </Button>
            ) : (
            <Button
              color="primary"
              onClick={() => sendQuestion()}
              disabled={loading || !question.trim()}
              style={iconButtonStyle}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <i className="fa fa-paper-plane" style={{ fontSize: '0.9rem' }}></i>
            </Button>
            )}
            {chatHistory.length > 0 && // No Trash icon when chat history is empty
              !isTyping && ( // Display Trash icon only when user is not typing
                <Button
                  onClick={(e) => requestDeleteChat(e)}
                  color="danger"
                  style={iconButtonStyle}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <i className="fa fa-trash" style={{ fontSize: '0.9rem' }}></i>
                </Button>
              )}
          </>
        )}
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmAction}
      >
        {`Are you sure you want to clear Chat History? Once deleted, all chat history will be lost.`}
      </ConfirmationModal>
    </div>
  );
};

Chat.propTypes = {
  conversationId: PropTypes.string,
  groupId: PropTypes.string,
  isActive: PropTypes.bool,
  inVisitPanel: PropTypes.bool,
  agentContext: PropTypes.arrayOf(PropTypes.string),
  agentTools: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string.isRequired, handler: PropTypes.func.isRequired }),
  ),
  apiClient: PropTypes.shape({
    patients: PropTypes.arrayOf(PropTypes.shape({ uid: PropTypes.string })),
    askChatQuestion: PropTypes.func,
    askChatQuestionStream: PropTypes.func,
    chatAgentStep: PropTypes.func,
    clearChatHistory: PropTypes.func,
    isTimeoutError: PropTypes.func,
    getChatHistory: PropTypes.func,
    transcribeAudio: PropTypes.func,
  }),
};

export default Chat;
